import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { useGetPresetGroup, useNavigateToPresetGroup } from '@restDataAccess';
import { Button, FlexAligner, IconButton, Typography } from '@scaleaq/scaleui';
import { PresetGroupInfo } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { FeedbackAlert, type FeedbackAlertProps, type FeedbackAlertState } from '@/components';
import { useOpenModal } from '@/hooks';
import { Icon, responsive } from '@/utils';

import { AddOrEditPresetGroupModal } from './AddOrEditPresetGroupModal.tsx';
import { RemovePresetGroupConfirmModal } from './RemovePresetGroupConfirmModal.tsx';

type PresetGroupsListProps = {
  alertProps: FeedbackAlertProps;
  setFeedback: Dispatch<SetStateAction<FeedbackAlertState | undefined>>;
};

export const PresetGroupsList = ({ alertProps, setFeedback }: PresetGroupsListProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['cameraControl', 'common']);
  const { data: presetGroupsData, isLoading } = useGetPresetGroup({});
  const { mutate: navigateToPresetGroup, isPending: isNavigating } = useNavigateToPresetGroup();
  const addModalController = useOpenModal();
  const removeModalController = useOpenModal();
  const [selectedPresetGroup, setSelectedPresetGroup] = useState<PresetGroupInfo>();
  const [presetGroupToDelete, setPresetGroupToDelete] = useState<PresetGroupInfo>();

  const presetGroups = useMemo(() => {
    const groups = presetGroupsData ?? [];
    return [...groups].sort((left, right) => left.name.localeCompare(right.name, language));
  }, [language, presetGroupsData]);

  const openPresetGroupForm = (presetGroup?: PresetGroupInfo) => {
    setSelectedPresetGroup(presetGroup);
    addModalController.handleOpenModal();
  };

  const openRemovePresetGroup = (presetGroup: PresetGroupInfo) => {
    setPresetGroupToDelete(presetGroup);
    removeModalController.handleOpenModal();
  };

  const showSavedFeedback = ({
    name,
    operation,
  }: {
    name: string;
    operation: keyof typeof savedPresetGroupMessageKey;
  }) => {
    setFeedback({ text: t(savedPresetGroupMessageKey[operation], { name }), variant: 'success' });
  };

  const handleNavigateToPresetGroup = (presetGroup: PresetGroupInfo) => {
    navigateToPresetGroup(
      { presetGroupId: presetGroup.id },
      {
        onSuccess: () =>
          setFeedback({
            text: t('preset.navigated', { name: presetGroup.name }),
            variant: 'success',
          }),
        onError: error => {
          const message = error instanceof Error ? error.message : '';
          setFeedback({
            text: t('preset.navigateFailed', { name: presetGroup.name, message }),
            variant: 'alarm',
          });
        },
      }
    );
  };

  return (
    <>
      <FeedbackAlert {...alertProps} loading={isLoading || isNavigating} />
      <FlexAligner direction={'column'} gap={responsive.spacing.large}>
        <FlexAligner alignment={'top-right'}>
          <AddOrEditPresetGroupModal
            modalController={addModalController}
            selectedPresetGroup={selectedPresetGroup}
            onDefineNew={() => openPresetGroupForm()}
            onSaved={showSavedFeedback}
          />
        </FlexAligner>
        {presetGroups.map(presetGroup => {
          const presetNames = (presetGroup.presets ?? [])
            .map(preset => preset.name)
            .sort((left, right) => left.localeCompare(right, language));

          return (
            <FlexAligner key={presetGroup.id} spaceBetween={true} alignment={'center'}>
              <FlexAligner
                direction={'column'}
                gap={responsive.spacing.xsmall}
                alignment={'top-left'}
              >
                <Typography type={'body_xlarge'}>{presetGroup.name}</Typography>
                {presetNames.length > 0 && (
                  <Typography type={'body_small'}>{presetNames.join(', ')}</Typography>
                )}
              </FlexAligner>
              <FlexAligner alignment={'center'} fullWidth={false} gap={responsive.spacing.large}>
                <IconButton
                  Icon={Icon.Trash2}
                  size={'medium'}
                  aria-label={t('common:delete')}
                  color={'alarm'}
                  onClick={() => openRemovePresetGroup(presetGroup)}
                />
                <IconButton
                  Icon={Icon.Pencil}
                  size={'medium'}
                  aria-label={t('common:edit')}
                  onClick={() => openPresetGroupForm(presetGroup)}
                />
                <Button
                  label={t('preset.goTo')}
                  disabled={isNavigating}
                  onClick={() => handleNavigateToPresetGroup(presetGroup)}
                  style={{ flexShrink: 0 }}
                />
              </FlexAligner>
            </FlexAligner>
          );
        })}
      </FlexAligner>
      {presetGroupToDelete && (
        <RemovePresetGroupConfirmModal
          modalController={removeModalController}
          onSaved={showSavedFeedback}
          presetGroup={presetGroupToDelete}
        />
      )}
    </>
  );
};

const savedPresetGroupMessageKey = {
  added: 'common:snackbar.success.added',
  updated: 'common:snackbar.success.updated',
  deleted: 'common:snackbar.success.deleted',
} as const;
